import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { AuthContext } from '../../legacyAuthContext';
import { useReleaseConfig } from '../../productRouting/releaseConfig';
import useProductSessions from '../../productRouting/useProductSessions';
import { EXTENSION_PATHS, TEAMS_PATHS } from '../../productRouting/legacyProductAuth';
import { V2_ROUTES } from '../../v2/routes';
import { useServerHtml } from '../../marketingRendering/InitialHtmlContext';

// Teams pages use the general product chooser for Sign in. Extension pages
// keep their own sign-in. After launch, an existing session opens its product.
// New teams contact sales: Routora Team has no self-serve sign-up.
const PRODUCT_DOORS = {
  teams: {
    signIn: '/signin',
    getStarted: TEAMS_PATHS.sales,
    getStartedLabel: 'Contact sales',
    open: TEAMS_PATHS.home,
    openLabel: 'Open Routora Team',
  },
  extension: {
    signIn: EXTENSION_PATHS.login,
    getStarted: EXTENSION_PATHS.setup,
    open: EXTENSION_PATHS.account,
    openLabel: 'Your extension account',
  },
};

const PERSONAL_DOORS = {
  signIn: '/signin',
  getStarted: '/signup',
  open: V2_ROUTES.dashboard,
  openLabel: 'Open Routora',
};

function MarketingNav({ logo, homeTo = '/', products: productsOverride, product }) {
  const serverHtml = useServerHtml();
  const history = useHistory();
  const location = useLocation();

  // Before launch the nav follows the legacy account, as it always has. After
  // launch Personal is Routora (V2) and Teams keeps its own sign-in, so each
  // session leads to its own product and neither chooses for the other.
  const { mode } = useReleaseConfig();
  const separateProducts = mode !== 'prelaunch';
  const legacyAccount = useContext(AuthContext);
  const sessions = useProductSessions({ enabled: separateProducts });
  const { signOutUser, getTeamsSubscription } = legacyAccount || {};
  const doors = PRODUCT_DOORS[product] || PERSONAL_DOORS;
  const productSession = PRODUCT_DOORS[product] ? sessions.legacy : sessions.personal;
  const currentUser = separateProducts ? productSession : legacyAccount?.currentUser;
  const products = productsOverride || (separateProducts ? getLaunchProducts(sessions.personal) : defaultProducts);
  const productsFooter = !productsOverride && separateProducts ? launchProductsFooter : null;
  const productIcons = products.some((item) => item.icon);

  const [scrolled, setScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
  }, []);

  function onSignOutClick() {
    signOutUser();
    history.push('/');
  }

  async function goProfile() {
    if (location.pathname === '/playground') {
      history.push('/profile');
      return;
    }
    if (currentUser) {
      let teamsSub = await getTeamsSubscription(currentUser.email);
      if (teamsSub && teamsSub.tier && teamsSub.tier >= 1) {
        history.push('/teams/settings/profile');
        return;
      }
    }
    history.push('/profile');
  }

  async function goTeams() {
    if (separateProducts) {
      // The Teams app sends signed-in people without a team to its intro.
      history.push(sessions.legacy ? '/teams' : '/teams-intro');
      return;
    }
    if (currentUser) {
      let teamsSub = await getTeamsSubscription(currentUser.email);
      if (teamsSub && teamsSub.tier && teamsSub.tier >= 1) {
        const today = new Date().toISOString().split('T')[0];
        history.push(`/teams/route/${today}`);
        return;
      }
    }
    history.push('/teams-intro');
  }

  function goAppInstall() {
    history.push('/app-install');
  }

  function goExtension() {
    window.open("https://chrome.google.com/webstore/detail/routora-google-maps-route/jdddfnfohdeaklgkpglonlofgapjgfbp", '_blank').focus();
  }

  function runProductAction(action) {
    if (action === 'teams') {
      goTeams();
    } else if (action === 'app-install') {
      goAppInstall();
    } else if (action === 'extension') {
      goExtension();
    }
  }

  let userText = separateProducts ? doors.openLabel : '';
  let userFirstChar = '';
  if (currentUser && currentUser.displayName) {
    userText = 'Hello ' + currentUser.displayName;
    if (currentUser.displayName.includes(' ')) {
      userText = 'Hello ' + currentUser.displayName.split(' ')[0];
    }
    userFirstChar = currentUser.displayName.charAt(0);
  }

  return (
    <nav className={`top${scrolled ? ' scrolled' : ''}${mobileOpen ? ' mobile-open' : ''}`} id="topnav">
      <div className="container inner">
        <Link to={homeTo} className="logo" aria-label="Routora">
          {logo}
        </Link>

        <div className="nav-links">
          <div
            className="nav-products-trigger"
            onMouseEnter={() => setIsProductsOpen(true)}
            onMouseLeave={() => setIsProductsOpen(false)}
          >
            <a href="#products">
              Products
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                expand_more
              </span>
            </a>

            {isProductsOpen && (
              <div className={`nav-products-dropdown${productIcons ? ' with-icons' : ''}`}>
                {products.map((product, index) => {
                  if (product.divider) {
                    return <div className="nav-dropdown-divider" role="separator" key={`divider-${index}`} />;
                  }
                  const itemContent = (
                    <>
                      {product.icon && (
                        <span className={`nav-dropdown-icon ${product.tone}`} aria-hidden="true">
                          <span className="material-symbols-outlined">{product.icon}</span>
                        </span>
                      )}
                      <div>
                        <span className="nav-dropdown-title">
                          {product.title}
                          {product.badge && (
                            <span className={`nav-dropdown-badge${product.badgeTone ? ` ${product.badgeTone}` : ''}`}>
                              {product.badge}
                            </span>
                          )}
                        </span>
                        <span className="nav-dropdown-sub">{product.description}</span>
                      </div>
                      {!product.icon && (
                        <span className="material-symbols-outlined nav-dropdown-arrow">
                          arrow_forward
                        </span>
                      )}
                    </>
                  );

                  if (product.to) {
                    return (
                      <Link to={product.to} className="nav-dropdown-item" key={product.title}>
                        {itemContent}
                      </Link>
                    );
                  }

                  return (
                    <div
                      onClick={() => runProductAction(product.action)}
                      className="nav-dropdown-item"
                      style={{ cursor: 'pointer' }}
                      key={product.title}
                    >
                      {itemContent}
                    </div>
                  );
                })}
                {productsFooter && (
                  <Link to={productsFooter.to} className="nav-dropdown-footer">{productsFooter.label}</Link>
                )}
              </div>
            )}
          </div>

          <Link to="/pricing">Pricing</Link>
        </div>

        <div className="nav-ctas">
          {separateProducts && currentUser ? (
            <Link to={doors.open} className="nav-profile-btn" title={doors.openLabel}>
              <span className="nav-profile-text">{userText}</span>
              {currentUser.photoURL ? (
                <img className="nav-profile-avatar" alt="" src={currentUser.photoURL} />
              ) : (
                <div className="nav-profile-initial">
                  {userFirstChar || (
                    <span className="material-symbols-outlined" aria-hidden="true">person</span>
                  )}
                </div>
              )}
            </Link>
          ) : currentUser ? (
            <>
              <md-text-button onClick={onSignOutClick}>Sign out</md-text-button>
              <div onClick={goProfile} className="nav-profile-btn" style={{ cursor: 'pointer' }}>
                <span className="nav-profile-text">{userText}</span>
                {currentUser.photoURL ? (
                  <img className="nav-profile-avatar" alt="" src={currentUser.photoURL} />
                ) : (
                  <div className="nav-profile-initial">{userFirstChar}</div>
                )}
              </div>
            </>
          ) : (
            <>
              <md-text-button onClick={serverHtml ? undefined : () => history.push(doors.signIn)}>Sign in</md-text-button>
              <md-filled-button onClick={serverHtml ? undefined : () => history.push(doors.getStarted)}>
                {doors.getStartedLabel || 'Get started'}
              </md-filled-button>
            </>
          )}
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          <span className="material-symbols-outlined">
            {mobileOpen ? 'close' : 'menu'}
          </span>
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`nav-mobile-drawer${mobileOpen ? ' open' : ''}`}>
        <div className="nav-mobile-inner">
          <div
            className="nav-mobile-item nav-mobile-products-trigger"
            onClick={() => setMobileProductsOpen((v) => !v)}
          >
            <span>Products</span>
            <span
              className="material-symbols-outlined nav-mobile-chevron"
              style={{ transform: mobileProductsOpen ? 'rotate(180deg)' : 'rotate(0)' }}
            >
              expand_more
            </span>
          </div>

          {mobileProductsOpen && (
            <div className="nav-mobile-products">
              {products.map((product, index) => {
                if (product.divider) {
                  return <div className="nav-mobile-divider" role="separator" key={`divider-${index}`} />;
                }
                if (product.to) {
                  return (
                    <Link
                      to={product.to}
                      className="nav-mobile-product-item"
                      key={product.title}
                      onClick={closeMobile}
                    >
                      <span className="nav-mobile-product-title">
                        {product.title}
                        {product.badge && (
                          <span className={`nav-dropdown-badge${product.badgeTone ? ` ${product.badgeTone}` : ''}`}>
                            {product.badge}
                          </span>
                        )}
                      </span>
                      <span className="nav-mobile-product-desc">{product.description}</span>
                    </Link>
                  );
                }
                return (
                  <div
                    className="nav-mobile-product-item"
                    key={product.title}
                    onClick={() => { runProductAction(product.action); closeMobile(); }}
                  >
                    <span className="nav-mobile-product-title">
                      {product.title}
                      {product.badge && (
                        <span className={`nav-dropdown-badge${product.badgeTone ? ` ${product.badgeTone}` : ''}`}>
                          {product.badge}
                        </span>
                      )}
                    </span>
                    <span className="nav-mobile-product-desc">{product.description}</span>
                  </div>
                );
              })}
            </div>
          )}

          <Link to="/pricing" className="nav-mobile-item" onClick={closeMobile}>
            Pricing
          </Link>

          <div className="nav-mobile-divider" />

          {separateProducts && currentUser ? (
            <Link to={doors.open} className="nav-mobile-item nav-mobile-cta" onClick={closeMobile}>
              {doors.openLabel}
            </Link>
          ) : currentUser ? (
            <>
              <div className="nav-mobile-item" onClick={() => { goProfile(); closeMobile(); }}>
                Profile
              </div>
              <div className="nav-mobile-item" onClick={() => { onSignOutClick(); closeMobile(); }}>
                Sign out
              </div>
            </>
          ) : (
            <>
              <Link to={doors.signIn} className="nav-mobile-item" onClick={closeMobile}>
                Sign in
              </Link>
              <Link to={doors.getStarted} className="nav-mobile-item nav-mobile-cta" onClick={closeMobile}>
                {doors.getStartedLabel || 'Get started'}
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

// After launch the menu is the inspection product lines: Routora Pro, which an
// inspector buys for themselves, and Routora Team, which an organization buys,
// are two ways to buy the same app; a divider sets off Routora for Government,
// a different product. Pro and Team open their respective apps for signed-in
// customers. The app and the Chrome extension aren't
// listed as products; their pages and sign-ins stay where they are.
const getLaunchProducts = (personalUser) => [
  {
    icon: 'person',
    tone: 'green',
    title: 'Routora Pro',
    description: 'For the inspector who plans their own day',
    to: personalUser ? PERSONAL_DOORS.open : '/web-intro',
  },
  {
    icon: 'groups',
    tone: 'blue',
    title: 'Routora Team',
    description: 'When your organization buys for its inspectors',
    badge: 'Soon',
    badgeTone: 'blue',
    action: 'teams',
  },
  { divider: true },
  {
    icon: 'account_balance',
    tone: 'purple',
    title: 'Routora for Government',
    description: 'For inspection agencies, on ArcGIS',
    to: '/gov-intro',
  },
];

const launchProductsFooter = { label: 'Compare plans', to: '/pricing' };

const defaultProducts = [
  {
    title: 'Routora for Government',
    description: 'Government agency solutions',
    to: '/gov-intro',
  },
  {
    title: 'Routora Team',
    description: 'Fleet route management',
    // Its next version is coming soon; new Teams subscriptions are closed.
    badge: 'Coming soon',
    action: 'teams',
  },
  {
    title: 'Web Route Planner',
    description: 'Individual route planner',
    to: '/playground',
  },
  {
    title: 'Mobile App',
    description: 'Driver app on iOS/Android',
    action: 'app-install',
  },
  {
    title: 'Chrome Extension',
    description: 'Google Maps route optimizer',
    action: 'extension',
  },
];

export default MarketingNav;
